/**
 * ActiveSessions — the devices a user is signed in on, with sign-out controls.
 *
 *   mode 'self'   → GET /auth/sessions (My Account). The current device is
 *                   flagged and can't be revoked here (that's Sign out).
 *   mode 'member' → GET /admin/user-management/users/:id/sessions (admin view
 *                   of a team member). Needs the userManagement permission.
 *
 * Revoking is immediate on the backend: the session's refresh token AND its
 * live access token stop working on that device's next request.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Computer from '@mui/icons-material/Computer';
import DevicesOther from '@mui/icons-material/DevicesOther';
import Logout from '@mui/icons-material/Logout';
import PhoneIphone from '@mui/icons-material/PhoneIphone';
import TabletMac from '@mui/icons-material/TabletMac';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';

import apiService from '@/services/api.service';

export interface SessionRow {
  id: string;
  current: boolean;
  createdAt: string;
  lastActiveAt: string;
  ip: string | null;
  lastIp: string | null;
  browser: string;
  os: string;
  deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
}

type Props =
  | { mode: 'self' }
  | { mode: 'member'; userId: string };

const DEVICE_ICON: Record<SessionRow['deviceType'], React.ReactNode> = {
  desktop: <Computer />,
  mobile: <PhoneIphone />,
  tablet: <TabletMac />,
  unknown: <DevicesOther />,
};

const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });

function extractError(error: any, fallback: string): string {
  return error?.response?.data?.error?.message || error?.message || fallback;
}

const ActiveSessions: React.FC<Props> = (props) => {
  const base =
    props.mode === 'self' ? '/auth/sessions' : `/admin/user-management/users/${props.userId}/sessions`;
  const revokeAllPath = props.mode === 'self' ? `${base}/revoke-others` : `${base}/revoke-all`;

  const [sessions, setSessions] = useState<SessionRow[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null); // session id, or 'all'

  const load = useCallback(async () => {
    try {
      const res = await apiService.get<{ data: { sessions: SessionRow[] } }>(base);
      setSessions(res?.data?.sessions ?? []);
      setError('');
    } catch (e: any) {
      setError(extractError(e, 'Could not load sessions'));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const revoke = async (key: string, path: string) => {
    setBusy(key);
    try {
      await apiService.post(path);
      await load();
    } catch (e: any) {
      setError(extractError(e, 'Could not sign out'));
    } finally {
      setBusy(null);
    }
  };

  if (sessions === null && !error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="error">{error}</Alert>}

      {sessions && sessions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No active sessions.
        </Typography>
      )}

      {sessions && sessions.length > 0 && (
        <List disablePadding>
          {sessions.map((s, i) => (
            <React.Fragment key={s.id}>
              {i > 0 && <Divider component="li" />}
              <ListItem
                sx={{ px: 0 }}
                secondaryAction={
                  s.current ? null : (
                    <Tooltip title="Sign out this device">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={busy !== null}
                          onClick={() => revoke(s.id, `${base}/${s.id}/revoke`)}
                          aria-label="Sign out this device"
                        >
                          {busy === s.id ? <CircularProgress size={16} /> : <Logout fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                }
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                  {DEVICE_ICON[s.deviceType]}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {s.browser} · {s.os}
                      </Typography>
                      {s.current && <Chip label="This device" size="small" color="primary" />}
                    </Stack>
                  }
                  secondary={
                    <>
                      {s.ip ? `Signed in from ${s.ip} ` : 'Signed in '}
                      {ago(s.createdAt)}
                      {s.lastIp && s.lastIp !== s.ip ? ` · last seen from ${s.lastIp}` : ''}
                      {' · active '}
                      {ago(s.lastActiveAt)}
                    </>
                  }
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Always offered, even when nothing else is listed: a sign-in from before
          session tracking only shows up here once that device is used again, but
          sign-out-all already covers it (backend per-user cutoff). */}
      {sessions && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Devices not used since session tracking was introduced appear here once they're
            next used. Signing out {props.mode === 'self' ? 'all other devices' : 'everywhere'} covers them too.
          </Typography>
          <Button
            sx={{ flexShrink: 0 }}
            variant="outlined"
            color="error"
            size="small"
            disabled={busy !== null}
            onClick={() => revoke('all', revokeAllPath)}
            startIcon={busy === 'all' ? <CircularProgress size={14} /> : <Logout />}
          >
            {props.mode === 'self' ? 'Sign out all other devices' : 'Sign out everywhere'}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default ActiveSessions;
